import React, { useState, useEffect, useRef } from 'react';
import { Account, AccountFormData } from '../types';
import { accountsAPI } from '../services/api';
import { parseCurrencyInput, formatCurrencyInput, getLiabilityCategories } from '../utils/formatters';
import InstitutionAutocomplete from './InstitutionAutocomplete';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FormLabel } from './ui/form-label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from './ui/select';
import { Calendar } from './ui/calendar';
import { SegmentedControl } from './ui/segmented-control';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

interface AddLiabilityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingAccount?: Account | null;
  onSuccess: () => void;
  existingAccounts?: Account[];
  isOnboarding?: boolean;
}

const CURRENCY_OPTIONS: { code: string; flag: string; name: string }[] = [
  { code: 'CAD', flag: '🇨🇦', name: 'Canadian Dollar' },
  { code: 'USD', flag: '🇺🇸', name: 'US Dollar' },
  { code: 'EUR', flag: '🇪🇺', name: 'Euro' },
  { code: 'GBP', flag: '🇬🇧', name: 'British Pound' },
  { code: 'JPY', flag: '🇯🇵', name: 'Japanese Yen' },
  { code: 'AUD', flag: '🇦🇺', name: 'Australian Dollar' },
  { code: 'CHF', flag: '🇨🇭', name: 'Swiss Franc' },
  { code: 'CNY', flag: '🇨🇳', name: 'Chinese Yuan' },
];

function getGroupedLiabilityCategories(
  accountType: 'personal' | 'business'
): { label: string; options: string[] }[] {
  if (accountType === 'business') {
    return [
      {
        label: 'Business Liabilities',
        options: getLiabilityCategories('business'),
      },
    ];
  }
  return [
    {
      label: 'Personal Liabilities',
      options: getLiabilityCategories('personal'),
    },
  ];
}

const parseValueInput = (raw: string): number | undefined => {
  if (raw.trim() === '' || !/\d/.test(raw)) return undefined;
  return parseCurrencyInput(raw);
};

const EMPTY_FORM: AccountFormData = {
  type: 'liability',
  account_type: 'personal',
  institution: '',
  name: '',
  category: '',
  currency: 'CAD',
  value: undefined,
  date: new Date().toISOString().split('T')[0],
  notes: '',
};

const AddLiabilityModal: React.FC<AddLiabilityModalProps> = ({
  open,
  onOpenChange,
  editingAccount = null,
  onSuccess,
  existingAccounts = [],
  isOnboarding = false,
}) => {
  const [formData, setFormData] = useState<AccountFormData>(EMPTY_FORM);
  const [currencyInputValue, setCurrencyInputValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ category?: string; name?: string; value?: string }>({});
  const [touched, setTouched] = useState<{ category?: boolean; name?: boolean; value?: boolean }>({});
  const [submitting, setSubmitting] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const [balanceDatePickerOpen, setBalanceDatePickerOpen] = useState(false);
  const balanceDateTriggerRef = useRef<HTMLButtonElement>(null);
  const balanceDateCalendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setBalanceDatePickerOpen(false);
  }, [open]);

  useEffect(() => {
    if (!balanceDatePickerOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !balanceDateTriggerRef.current?.contains(target) &&
        !balanceDateCalendarRef.current?.contains(target)
      ) {
        setBalanceDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [balanceDatePickerOpen]);

  useEffect(() => {
    if (!open) return;
    if (editingAccount) {
      setFormData({
        type: editingAccount.type,
        account_type: editingAccount.account_type,
        institution: editingAccount.institution,
        name: editingAccount.name,
        category: editingAccount.category,
        currency: editingAccount.currency,
        value: editingAccount.current_value,
        date: editingAccount.latest_snapshot_date || new Date().toISOString().split('T')[0],
        notes: editingAccount.notes || '',
      });
      setCurrencyInputValue(
        editingAccount.current_value ? formatCurrencyInput(editingAccount.current_value) : ''
      );
      setFormDirty(false);
    } else {
      resetForm();
    }
  }, [open, editingAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setFormDirty(false);
    setFormData({ ...EMPTY_FORM, date: new Date().toISOString().split('T')[0] });
    setCurrencyInputValue('');
    setFieldErrors({});
    setTouched({});
    setSubmitting(false);
  };

  const handleClose = () => {
    if (formDirty) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
      resetForm();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const errors: { category?: string; name?: string; value?: string } = {};
    if (!String(formData.category ?? '').trim()) errors.category = 'Liability category is required.';
    if (!String(formData.name ?? '').trim()) errors.name = 'Account name is required.';
    if (!editingAccount && formData.value == null) errors.value = 'Balance amount is required.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      if (editingAccount) {
        await accountsAPI.updateAccount(editingAccount.id, {
          type: formData.type,
          account_type: formData.account_type,
          institution: formData.institution,
          name: formData.name,
          category: formData.category,
          currency: formData.currency,
          notes: formData.notes,
        });
      } else {
        await accountsAPI.addAccount(formData);
      }

      onOpenChange(false);
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error saving liability:', error);
      alert('Failed to save liability. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const existingInstitutions = existingAccounts.map((a) => a.institution).filter(Boolean) as string[];

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-dialog-content border border-border shadow-2xl">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Liability' : 'Add Liability'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} noValidate className="space-y-4 add-asset-form">
            {/* Personal / Business toggle */}
            <div className="flex flex-col gap-2">
              <FormLabel className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</FormLabel>
              <SegmentedControl
                aria-label="Account type"
                value={formData.account_type}
                onValueChange={(v) => {
                  setFormDirty(true);
                  setFormData({ ...formData, account_type: v, category: '' });
                }}
                options={[
                  { value: 'personal', label: 'Personal', icon: '👤' },
                  { value: 'business', label: 'Business', icon: '🏢' },
                ]}
              />
            </div>

            {/* Liability category */}
            <div className="space-y-2">
              <FormLabel htmlFor="category">Liability Category</FormLabel>
              <Select
                value={formData.category}
                onOpenChange={(o) => { if (o) setTouched((prev) => ({ ...prev, category: true })); }}
                onValueChange={(val) => {
                  setFormDirty(true);
                  setTouched((prev) => ({ ...prev, category: true }));
                  if (String(val).trim()) setFieldErrors((prev) => ({ ...prev, category: undefined }));
                  setFormData({ ...formData, category: val });
                }}
              >
                <SelectTrigger
                  id="category"
                  className={cn('w-full', fieldErrors.category && 'border-red-400')}
                  onBlur={() => {
                    if (!touched.category) return;
                    if (!String(formData.category ?? '').trim()) {
                      setFieldErrors((prev) => ({ ...prev, category: 'Liability category is required.' }));
                    }
                  }}
                >
                  <SelectValue placeholder="Select a liability category" />
                </SelectTrigger>
                <SelectContent>
                  {getGroupedLiabilityCategories(formData.account_type).map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.options.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.category && <p className="text-xs text-red-400">{fieldErrors.category}</p>}
            </div>

            {/* Institution */}
            <div className="space-y-2">
              <FormLabel htmlFor="institution" optional>Institution</FormLabel>
              <InstitutionAutocomplete
                value={formData.institution}
                onChange={(value) => { setFormDirty(true); setFormData({ ...formData, institution: value }); }}
                existingInstitutions={existingInstitutions}
                placeholder="e.g., TD Bank, RBC, etc."
              />
            </div>

            {/* Account name */}
            <div className="space-y-2">
              <FormLabel htmlFor="liability-account-name">Account Name</FormLabel>
              <Input
                id="liability-account-name"
                name="accountLabel"
                autoComplete="off"
                value={formData.name}
                onChange={(e) => {
                  setTouched((prev) => ({ ...prev, name: true }));
                  const val = e.target.value;
                  if (String(val).trim()) setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  setFormDirty(true);
                  setFormData({ ...formData, name: val });
                }}
                onBlur={(e) => {
                  if (!touched.name) return;
                  if (!String(e.target.value).trim()) {
                    setFieldErrors((prev) => ({ ...prev, name: 'Account name is required.' }));
                  }
                }}
                placeholder="e.g., Mortgage, TD Visa, Home Equity Line, etc."
                className={cn('bg-[var(--background-dark)]', fieldErrors.name && 'border-red-400')}
              />
              {fieldErrors.name && <p className="text-xs text-red-400">{fieldErrors.name}</p>}
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <FormLabel htmlFor="currency">Currency</FormLabel>
              <Select
                value={formData.currency}
                onValueChange={(val) => { setFormDirty(true); setFormData({ ...formData, currency: val }); }}
              >
                <SelectTrigger id="currency"><SelectValue placeholder="Select currency..." /></SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((cur) => (
                    <SelectItem key={cur.code} value={cur.code}>
                      <span className="flex items-center gap-2">
                        <span>{cur.flag}</span>
                        <span className="font-medium">{cur.code}</span>
                        <span className="text-muted-foreground font-normal ml-2">{cur.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Balance date + value — hidden in onboarding and edit mode */}
            {!isOnboarding && !editingAccount && (
              <>
                <div className="space-y-2 relative">
                  <FormLabel htmlFor="balance-date-trigger">Balance Date</FormLabel>
                  <Button
                    ref={balanceDateTriggerRef}
                    type="button"
                    id="balance-date-trigger"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal bg-[var(--background-dark)] hover:bg-[var(--background-dark)] gap-2',
                      formData.date
                        ? 'text-foreground hover:text-foreground'
                        : 'text-muted-foreground/80 hover:text-muted-foreground/80'
                    )}
                    onClick={() => setBalanceDatePickerOpen((o) => !o)}
                    aria-expanded={balanceDatePickerOpen}
                  >
                    <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                    {formData.date ? format(parseISO(formData.date), 'MMM d, yyyy') : 'Pick a date'}
                  </Button>
                  {balanceDatePickerOpen && (
                    <div
                      ref={balanceDateCalendarRef}
                      className="balance-date-inline absolute left-0 top-full z-50 w-fit rounded-md border border-input bg-[var(--background-dark)] p-2"
                    >
                      <Calendar
                        mode="single"
                        selected={formData.date ? parseISO(formData.date) : undefined}
                        onSelect={(selected) => {
                          if (selected) {
                            setFormData({ ...formData, date: format(selected, 'yyyy-MM-dd') });
                            setBalanceDatePickerOpen(false);
                            requestAnimationFrame(() => {
                              const next = document.getElementById('value') as HTMLInputElement | null;
                              next?.focus();
                            });
                          }
                        }}
                        defaultMonth={formData.date ? parseISO(formData.date) : new Date()}
                        disabled={(date) => date > new Date()}
                        fixedWeeks
                        captionLayout="dropdown"
                        startMonth={new Date(new Date().getFullYear() - 10, 0)}
                        endMonth={new Date()}
                        hideNavigation
                        className="p-2 text-sm bg-[var(--background-dark)]"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <FormLabel htmlFor="value">
                    Balance on {formData.date ? format(parseISO(formData.date), 'MMM d, yyyy') : 'selected date'}
                  </FormLabel>
                  <Input
                    type="text"
                    id="value"
                    value={currencyInputValue}
                    onChange={(e) => {
                      setFormDirty(true);
                      setTouched((prev) => ({ ...prev, value: true }));
                      const raw = e.target.value;
                      setCurrencyInputValue(raw);
                      const newValue = parseValueInput(raw);
                      setFormData({ ...formData, value: newValue });
                      if (newValue != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                    }}
                    onBlur={(e) => {
                      const raw = e.target.value;
                      const newValue = parseValueInput(raw);
                      setFormData({ ...formData, value: newValue });
                      setCurrencyInputValue(newValue != null ? formatCurrencyInput(newValue) : '');
                      if (!touched.value) return;
                      if (newValue == null) {
                        setFieldErrors((prev) => ({ ...prev, value: 'Balance amount is required.' }));
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      setFormDirty(true);
                      const text = e.clipboardData.getData('text/plain').trim();
                      const parsed = parseValueInput(text);
                      setFormData((prev) => ({ ...prev, value: parsed }));
                      setCurrencyInputValue(text === '' ? '' : (parsed != null ? formatCurrencyInput(parsed) : ''));
                      if (parsed != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                    }}
                    placeholder="e.g., $1,234.56 or 1234.56"
                    className={cn('bg-[var(--background-dark)]', fieldErrors.value && 'border-red-400')}
                  />
                  {fieldErrors.value && <p className="text-xs text-red-400">{fieldErrors.value}</p>}
                </div>
              </>
            )}

            {/* Onboarding: simplified balance input (no date picker) */}
            {isOnboarding && !editingAccount && (
              <div className="space-y-2">
                <FormLabel htmlFor="value">Current Balance</FormLabel>
                <Input
                  type="text"
                  id="value"
                  value={currencyInputValue}
                  onChange={(e) => {
                    setFormDirty(true);
                    setTouched((prev) => ({ ...prev, value: true }));
                    const raw = e.target.value;
                    setCurrencyInputValue(raw);
                    const newValue = parseValueInput(raw);
                    setFormData({ ...formData, value: newValue });
                    if (newValue != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const newValue = parseValueInput(raw);
                    setFormData({ ...formData, value: newValue });
                    setCurrencyInputValue(raw.trim() === '' ? '' : formatCurrencyInput(newValue as number));
                    if (!touched.value) return;
                    if (newValue == null) {
                      setFieldErrors((prev) => ({ ...prev, value: 'Balance amount is required.' }));
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    setFormDirty(true);
                    const text = e.clipboardData.getData('text/plain').trim();
                    const parsed = parseValueInput(text);
                    setFormData((prev) => ({ ...prev, value: parsed }));
                    setCurrencyInputValue(text === '' ? '' : (parsed != null ? formatCurrencyInput(parsed) : ''));
                    if (parsed != null) setFieldErrors((prev) => ({ ...prev, value: undefined }));
                  }}
                  placeholder="e.g., $1,234.56 or 1234.56"
                  className={cn('bg-[var(--background-dark)]', fieldErrors.value && 'border-red-400')}
                />
                {fieldErrors.value && <p className="text-xs text-red-400">{fieldErrors.value}</p>}
              </div>
            )}

            {/* Notes — hidden in onboarding */}
            {!isOnboarding && (
              <div className="space-y-2">
                <FormLabel htmlFor="notes" optional>Notes</FormLabel>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => { setFormDirty(true); setFormData({ ...formData, notes: e.target.value }); }}
                  placeholder="Optional notes about this liability"
                  rows={3}
                  className="bg-[var(--background-dark)]"
                />
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <span className="loading-text flex items-center gap-2">
                    <span className="loading-spinner" />
                    {editingAccount ? 'Updating...' : 'Adding...'}
                  </span>
                ) : (
                  editingAccount ? 'Update Liability' : 'Add Liability'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Discard confirmation */}
      <Dialog open={showDiscardConfirm} onOpenChange={(o) => { if (!o) setShowDiscardConfirm(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Discard changes?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">You have unsaved changes. Discard them and close the form?</p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="secondary" onClick={() => setShowDiscardConfirm(false)}>
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setShowDiscardConfirm(false);
                onOpenChange(false);
                resetForm();
              }}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddLiabilityModal;
